/**
 * expenseUtils.js — pure helpers for the monthly Expense Calculator.
 *
 * Parses a Chase activity CSV into editable expense rows, auto-categorizes the
 * store from the merchant text (with user-learned overrides), and rolls the
 * rows up into monthly totals. No React, no I/O — fully unit-tested.
 */

export const EXPENSE_HEADERS = ['Date', 'Items', 'Store', 'Cost', 'Personal Cost'];

// ── CSV parsing ───────────────────────────────────────────────────────────────

/** Parse CSV text into a 2-D array of rows, honoring quoted fields. */
export function parseCsv(text) {
  const s = (text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows = [];
  let row = [], field = '', inQuotes = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') { field += '"'; i++; } // escaped quote
        else inQuotes = false;
      } else field += c;
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field); field = '';
    } else if (c === '\n') {
      row.push(field); rows.push(row); row = []; field = '';
    } else {
      field += c;
    }
  }
  if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
  // Drop blank lines.
  return rows.filter(r => r.some(cell => (cell || '').trim() !== ''));
}

/** Find a column index by exact then partial header match (case-insensitive). */
function findCol(header, ...names) {
  const lower = header.map(h => (h || '').toLowerCase().trim());
  for (const name of names) {
    const i = lower.indexOf(name);
    if (i >= 0) return i;
  }
  for (const name of names) {
    const i = lower.findIndex(h => h.includes(name));
    if (i >= 0) return i;
  }
  return -1;
}

// ── Store auto-categorization (with learned overrides) ───────────────────────

// Default keyword → store rules, tuned to this business's merchants.
const STORE_RULES = [
  { kw: ['walmart', 'wal-mart', 'wal mart', 'wm supercenter', 'wm superctr', 'wm superc'], store: 'walmart' },
  { kw: ["sam's", 'sams club', 'sams', 'sam club'], store: 'sams' },
  { kw: ['costco'], store: 'costco' },
  { kw: ['amazon', 'amzn'], store: 'amazon' },
  { kw: ['at&t', 'at and t', 'att*', 'att '], store: 'at&t' },
  { kw: ['bharat', 'bharath', 'desibazzar', 'desi bazzar', 'desibazaar', 'namesthe', 'india mart', 'indian', 'patel'], store: 'indian grocery' },
  { kw: ['propane', 'ozark'], store: 'propane' },
  { kw: ['square', 'sq *', 'sq*', 'pos '], store: 'pos' },
];

/** Normalized merchant key for learning (strips store numbers / symbols). */
export function merchantKey(description) {
  return (description || '').toLowerCase().replace(/[#*0-9]/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Best-guess store label for a merchant description.
 * Learned `overrides` (keyed by merchantKey) win over the default rules.
 */
export function categorizeStore(description, overrides = {}) {
  const key = merchantKey(description);
  if (overrides && key && overrides[key]) return overrides[key];
  const lower = (description || '').toLowerCase();
  for (const rule of STORE_RULES) {
    if (rule.kw.some(k => lower.includes(k))) return rule.store;
  }
  return '';
}

// ── Chase CSV → expense rows ─────────────────────────────────────────────────

let _seq = 0;
function makeId() { _seq += 1; return `e${Date.now().toString(36)}_${_seq}`; }

const toNum = v => {
  const n = parseFloat(String(v == null ? '' : v).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : NaN;
};
const round2 = n => Math.round(n * 100) / 100;

/**
 * Map a Chase activity CSV into editable expense rows.
 * - Date  ← Transaction Date
 * - Items ← Description
 * - Store ← auto-categorized from Description
 * - Cost  ← |Amount|  (card "Payment" rows are skipped — not an expense)
 * - Personal Cost ← blank (filled in by the user)
 *
 * @param {string} text
 * @param {Object} [overrides] learned merchantKey → store map
 * @returns {Array<{id,date,items,store,cost,personalCost}>}
 */
export function parseChaseCsv(text, overrides = {}) {
  const all = parseCsv(text);
  if (all.length < 2) return [];
  const header = all[0];
  // Use the TRANSACTION date (when the purchase was made), falling back to post date.
  const dateCol = findCol(header, 'transaction date', 'trans date', 'post date', 'posting date', 'date');
  const descCol = findCol(header, 'description', 'merchant', 'name');
  const amtCol = findCol(header, 'amount', 'debit');
  const typeCol = findCol(header, 'type');

  const rows = [];
  for (let i = 1; i < all.length; i++) {
    const r = all[i];
    const type = typeCol >= 0 ? (r[typeCol] || '').toLowerCase().trim() : '';
    if (type === 'payment') continue; // paying the card off is not an expense
    const amt = toNum(amtCol >= 0 ? r[amtCol] : '');
    if (!Number.isFinite(amt) || amt === 0) continue;
    const desc = ((descCol >= 0 ? r[descCol] : '') || '').trim();
    rows.push({
      id: makeId(),
      date: ((dateCol >= 0 ? r[dateCol] : '') || '').trim(),
      items: desc,
      store: categorizeStore(desc, overrides),
      cost: round2(Math.abs(amt)),
      personalCost: '',
    });
  }
  return rows;
}

/** A fresh blank row for manual entry. */
export function blankRow() {
  return { id: makeId(), date: '', items: '', store: '', cost: '', personalCost: '' };
}

// ── Universal CSV import (Chase activity OR this app's exported format) ──────

/**
 * Sniff which CSV this is from its header row:
 *   'export' — this app's own format (Date, Items, Store, Cost, Personal Cost)
 *   'chase'  — a bank activity export (Description / Amount / Type columns)
 */
export function detectExpenseCsvFormat(text) {
  const header = (parseCsv(text)[0] || []).map(h => (h || '').toLowerCase().trim());
  const has = name => header.some(h => h === name || h.includes(name));
  if (has('items') || has('personal')) return 'export';
  return 'chase';
}

/**
 * Parse this app's exported expense CSV (Date, Items, Store, Cost, Personal Cost)
 * back into editable rows. A missing Store is auto-categorized so re-imports of
 * hand-made sheets still get labeled.
 */
export function parseExportCsv(text, overrides = {}) {
  const all = parseCsv(text);
  if (all.length < 2) return [];
  const header = all[0];
  const dateCol = findCol(header, 'date');
  const itemsCol = findCol(header, 'items', 'item', 'description');
  const storeCol = findCol(header, 'store');
  const costCol = findCol(header, 'cost', 'amount');
  const persCol = findCol(header, 'personal cost', 'personal');
  const cell = (r, i) => ((i >= 0 ? r[i] : '') || '').trim();

  const rows = [];
  for (let i = 1; i < all.length; i++) {
    const r = all[i];
    const items = cell(r, itemsCol);
    // "Cost" and "Personal Cost" both match findCol('cost'), so guard the overlap.
    const costIdx = costCol === persCol ? -1 : costCol;
    const cost = toNum(cell(r, costIdx));
    if (!items && !Number.isFinite(cost)) continue; // separator / total-only line
    const pers = toNum(cell(r, persCol));
    const store = cell(r, storeCol);
    rows.push({
      id: makeId(),
      date: cell(r, dateCol),
      items,
      store: store || categorizeStore(items, overrides),
      cost: Number.isFinite(cost) ? round2(Math.abs(cost)) : '',
      personalCost: Number.isFinite(pers) && pers !== 0 ? round2(Math.abs(pers)) : '',
    });
  }
  return rows;
}

/**
 * One entry point for any expense CSV: detects the format, parses with the
 * right parser, and says which one it used.
 * @returns {{ rows: Array, format: 'chase'|'export' }}
 */
export function parseExpenseCsv(text, overrides = {}) {
  const format = detectExpenseCsvFormat(text);
  const rows = format === 'export' ? parseExportCsv(text, overrides) : parseChaseCsv(text, overrides);
  return { rows, format };
}

/** Duplicate-detection fingerprint: same day + same description + same cost. */
export function expenseRowKey(r) {
  const date = toISODate(r?.date) || String(r?.date || '').trim();
  return [date, String(r?.items || '').trim().toLowerCase(), String(num(r?.cost))].join('|');
}

/**
 * Merge freshly-imported rows into a month, skipping duplicates so the same
 * statement can be uploaded twice without double-counting. Duplicates are
 * matched per-occurrence (a multiset), so two genuinely identical purchases
 * inside one file still both import on the first upload.
 * @returns {{ rows: Array, added: number, skipped: number }}
 */
export function mergeExpenseRows(existing, incoming) {
  const seen = new Map();
  for (const r of (existing || [])) {
    const k = expenseRowKey(r);
    seen.set(k, (seen.get(k) || 0) + 1);
  }
  const rows = [...(existing || [])];
  let added = 0, skipped = 0;
  for (const r of (incoming || [])) {
    const k = expenseRowKey(r);
    const count = seen.get(k) || 0;
    if (count > 0) { seen.set(k, count - 1); skipped += 1; continue; }
    rows.push(r); added += 1;
  }
  return { rows, added, skipped };
}

/** Human month label ("May 2026") from the most common month in the rows' dates. */
export function monthLabelFromRows(rows) {
  const tally = {};
  for (const r of (rows || [])) {
    const iso = toISODate(r?.date);
    if (iso) tally[iso.slice(0, 7)] = (tally[iso.slice(0, 7)] || 0) + 1;
  }
  const best = Object.entries(tally).sort((a, b) => b[1] - a[1])[0];
  if (!best) return '';
  const [y, m] = best[0].split('-');
  return MONTH_NAMES[parseInt(m, 10) - 1] ? `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}` : '';
}

// ── Learned store names (from current edits + saved history) ──────────────────

/** Build a { merchantKey: store } map from rows that have a store assigned. */
export function learnedFromRows(rows) {
  const map = {};
  for (const r of (rows || [])) {
    const store = (r.store || '').trim();
    const key = merchantKey(r.items);
    if (store && key) map[key] = store;
  }
  return map;
}

/**
 * Combine everything we've learned into one { merchantKey: store } map:
 * every saved history month, then the explicit overrides on top (latest wins).
 */
export function buildLearnedMap(history, overrides) {
  const map = {};
  for (const entry of (history || [])) Object.assign(map, learnedFromRows(entry?.rows));
  Object.assign(map, overrides || {});
  return map;
}

/** The built-in store categories (always offered as autocomplete suggestions). */
export const DEFAULT_STORES = ['walmart', 'sams', 'costco', 'amazon', 'at&t', 'indian grocery', 'propane', 'pos'];

/**
 * Sorted, de-duplicated list of every store name seen — defaults + current rows
 * + all saved history + learned overrides — for the edit autocomplete.
 */
export function knownStores(rows, history, overrides) {
  const set = new Set(DEFAULT_STORES);
  const add = s => { const v = (s || '').trim(); if (v) set.add(v); };
  (rows || []).forEach(r => add(r.store));
  (history || []).forEach(e => (e?.rows || []).forEach(r => add(r.store)));
  Object.values(overrides || {}).forEach(add);
  return [...set].sort((a, b) => a.localeCompare(b));
}

/** Set the store on every row that shares the same merchant (when editing one). */
export function applyStoreToMerchant(rows, items, store) {
  const key = merchantKey(items);
  if (!key) return rows || [];
  return (rows || []).map(r => (merchantKey(r.items) === key ? { ...r, store } : r));
}

// ── Totals / clarity ─────────────────────────────────────────────────────────

const num = v => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

/**
 * Roll rows up into monthly totals + a per-store breakdown.
 * Business = total spend − personal.
 */
export function computeTotals(rows) {
  const list = Array.isArray(rows) ? rows : [];
  let total = 0, personal = 0;
  const storeMap = {};
  for (const r of list) {
    const c = num(r.cost);
    total += c;
    personal += num(r.personalCost);
    const store = (r.store || '').trim() || 'uncategorized';
    storeMap[store] = (storeMap[store] || 0) + c;
  }
  const byStore = Object.entries(storeMap)
    .map(([store, amount]) => ({ store, amount: round2(amount) }))
    .sort((a, b) => b.amount - a.amount || a.store.localeCompare(b.store));
  return {
    count: list.length,
    total: round2(total),
    personal: round2(personal),
    business: round2(total - personal),
    byStore,
  };
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

/** Derive a human month label from a Chase filename like *_Activity20260501_*. */
export function parseStatementMonth(filename) {
  const m = (filename || '').match(/(\d{4})(\d{2})(\d{2})/);
  if (!m) return '';
  const idx = parseInt(m[2], 10) - 1;
  return MONTH_NAMES[idx] ? `${MONTH_NAMES[idx]} ${m[1]}` : '';
}

/** Row → cell array for CSV export (parallel to EXPENSE_HEADERS). */
export function rowToCells(r) {
  return [r.date, r.items, r.store, r.cost, r.personalCost];
}

// ── Date conversion (for the <input type="date"> picker) ─────────────────────

/** Any date string → ISO 'YYYY-MM-DD' (for the date picker value). '' if unknown. */
export function toISODate(s) {
  const str = String(s == null ? '' : s).trim();
  if (!str) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/); // M/D/Y
  if (m) {
    const yy = m[3].length === 2 ? '20' + m[3] : m[3];
    return `${yy}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
  }
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

/** ISO 'YYYY-MM-DD' → US 'MM/DD/YYYY' (how dates are stored/exported). */
export function toMDY(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : String(iso || '');
}

/** Sorted, de-duplicated item/merchant names from current rows + all history. */
export function knownItems(rows, history) {
  const set = new Set();
  const add = s => { const v = (s || '').trim(); if (v) set.add(v); };
  (rows || []).forEach(r => add(r.items));
  (history || []).forEach(e => (e?.rows || []).forEach(r => add(r.items)));
  return [...set].sort((a, b) => a.localeCompare(b));
}

// ── Sorting & filtering (table view) ─────────────────────────────────────────

const NUMERIC_KEYS = new Set(['cost', 'personalCost']);

/** Sort a copy of rows by a column key ('date'|'items'|'store'|'cost'|'personalCost'). */
export function sortRows(rows, key, dir = 'asc') {
  const sign = dir === 'desc' ? -1 : 1;
  const get = (r) => {
    if (key === 'date') return Date.parse(r.date) || 0;
    if (NUMERIC_KEYS.has(key)) { const n = parseFloat(r[key]); return Number.isFinite(n) ? n : 0; }
    return String(r[key] || '').toLowerCase();
  };
  return [...(rows || [])].sort((a, b) => {
    const av = get(a), bv = get(b);
    if (av < bv) return -1 * sign;
    if (av > bv) return 1 * sign;
    return 0;
  });
}

/**
 * Filter rows by a free-text query (date/items/store) and a store match.
 * The special storeFilter 'uncategorized' matches rows with no store set.
 */
export function filterRows(rows, query = '', storeFilter = '') {
  const q = (query || '').trim().toLowerCase();
  const sf = (storeFilter || '').trim();
  return (rows || []).filter(r => {
    const store = (r.store || '').trim();
    if (sf) {
      if (sf === 'uncategorized' ? store !== '' : store !== sf) return false;
    }
    if (!q) return true;
    return [r.date, r.items, r.store].some(v => String(v || '').toLowerCase().includes(q));
  });
}

/** How many rows still have no store assigned (need review). */
export function uncategorizedCount(rows) {
  return (rows || []).filter(r => !(r.store || '').trim()).length;
}

// ── Monthly settle-up (revenue → net profit) ─────────────────────────────────

/** Default revenue channels for this business (seeded on a fresh month). */
export const DEFAULT_REVENUE = ['Square', 'Cash & catering', 'Uber Eats', 'DoorDash', 'Grubhub'];
/** Default extra deductions beyond the tracked expense table. */
export const DEFAULT_DEDUCTIONS = ['Cash entered', 'Uday', 'Square Charges'];

let _sseq = 0;
function makeSettleId() { _sseq += 1; return `s${Date.now().toString(36)}_${_sseq}`; }

/** A fresh blank settle line (a revenue channel or a deduction). */
export function blankSettleLine(name = '', amount = '') {
  return { id: makeSettleId(), name, amount };
}

/** Seed a settle object with the business's default channels/deductions (amounts blank). */
export function defaultSettle() {
  return {
    revenue: DEFAULT_REVENUE.map(n => blankSettleLine(n)),
    deductions: DEFAULT_DEDUCTIONS.map(n => blankSettleLine(n)),
  };
}

/** Back-fill a well-formed settle object for legacy months (or bad data). */
export function ensureSettle(settle) {
  if (!settle || typeof settle !== 'object') return defaultSettle();
  const fix = list => (Array.isArray(list) ? list.map(l => ({
    id: l && l.id ? l.id : makeSettleId(),
    name: l ? (l.name || '') : '',
    amount: l ? (l.amount ?? '') : '',
  })) : []);
  return { revenue: fix(settle.revenue), deductions: fix(settle.deductions) };
}

/**
 * Monthly settle-up: reconcile every revenue channel against total costs.
 *   costs      = tracked business expenses (from the expense table) + manual deductions
 *   net profit = gross revenue − costs
 * `trackedExpenses` is the business total from computeTotals(rows).business, so the
 * settle-up stays linked to whatever is entered on the expense table.
 */
export function computeSettle(settle, trackedExpenses = 0) {
  const s = ensureSettle(settle);
  const gross = round2(s.revenue.reduce((a, r) => a + num(r.amount), 0));
  const deductions = round2(s.deductions.reduce((a, r) => a + num(r.amount), 0));
  const tracked = round2(num(trackedExpenses));
  const costs = round2(tracked + deductions);
  const net = round2(gross - costs);
  const margin = gross ? Math.round((net / gross) * 1000) / 10 : 0;
  const byChannel = s.revenue
    .map(r => ({ name: (r.name || '').trim() || '—', amount: round2(num(r.amount)) }))
    .filter(r => r.amount !== 0)
    .sort((a, b) => b.amount - a.amount);
  return { gross, deductions, tracked, costs, net, margin, byChannel };
}

/** Column headers for the settle-up CSV (label + amount). */
export const SETTLE_HEADERS = ['Cash type', 'Amount'];

const SETTLE_SKIP = /^(net|total|profit|balance|net\s*profit|grand\s*total|sum)\b/i;

/**
 * Parse a two-column summary CSV (label, amount) into settle-up lines — the same
 * shape as the classic "cash type / Amount" sheet:
 *   • positive amounts  → revenue channels
 *   • negative amounts  → deductions (stored as positive magnitudes)
 *   • header row, blank separators, and total/net rows are ignored
 * The amount is taken from the first numeric cell after the label, so extra
 * trailing columns don't matter.
 */
export function parseSettleCsv(text) {
  const all = parseCsv(text);
  const revenue = [], deductions = [];
  for (const r of all) {
    const label = String(r[0] == null ? '' : r[0]).trim();
    if (!label || SETTLE_SKIP.test(label)) continue;   // separator / total-only / net row
    let amt = NaN;
    for (let i = 1; i < r.length; i++) { const n = toNum(r[i]); if (Number.isFinite(n)) { amt = n; break; } }
    if (!Number.isFinite(amt)) continue;               // header row ("Amount") or text-only row
    if (amt >= 0) revenue.push(blankSettleLine(label, round2(amt)));
    else deductions.push(blankSettleLine(label, round2(Math.abs(amt))));
  }
  return { revenue, deductions };
}

/**
 * Settle object → 2-column rows for CSV export, mirroring the source sheet:
 * revenue positive, tracked expenses + deductions negative, blank row, then Net.
 */
export function settleToCsvRows(settle, trackedExpenses = 0) {
  const s = ensureSettle(settle);
  const res = computeSettle(s, trackedExpenses);
  const rows = [];
  s.revenue.forEach(r => rows.push([r.name || '', num(r.amount)]));
  rows.push(['', '']);
  if (trackedExpenses) rows.push(['expenses (tracked)', -round2(num(trackedExpenses))]);
  s.deductions.forEach(d => rows.push([d.name || '', -round2(num(d.amount))]));
  rows.push(['', '']);
  rows.push(['net profit', res.net]);
  return rows;
}

/**
 * First-sync reconciliation between this device's local copy and the cloud doc.
 * Prevents losing edits made offline:
 *   'seed'  — no cloud doc yet → upload local
 *   'adopt' — cloud is newer or equal → take the cloud copy
 *   'push'  — local is newer (edited offline) → keep local and upload it
 * (Legacy — the screen now uses mergeExpenseState, which can't drop months.)
 */
export function reconcileExpense(localUpdatedMs, cloud) {
  if (!cloud) return 'seed';
  const cloudMs = Number(cloud.updatedAtMs) || 0;
  return cloudMs >= (Number(localUpdatedMs) || 0) ? 'adopt' : 'push';
}

// ── Merge-based sync (per-month, cannot silently drop a month) ───────────────

/** How long a deleted month stays restorable in the trash. */
export const TRASH_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/** When was this month last touched (edit or save)? */
const monthFresh = m => Math.max(Number(m?.updatedAt) || 0, Number(m?.savedAt) || 0);

/** Drop trash entries older than the 30-day restore window. */
export function purgeTrash(trash, now = Date.now()) {
  return (Array.isArray(trash) ? trash : [])
    .filter(t => t && t.id && now - (Number(t.deletedAt) || 0) < TRASH_TTL_MS);
}

/**
 * Merge the local and cloud copies of the expense state on (re)connect.
 * Unlike adopt/push, a merge can never silently drop a month:
 *   • months  — union by id; when both sides have one, the fresher edit wins
 *   • trash   — union by id (latest deletion wins); acts as tombstones, so a
 *               month deleted on one device stays deleted, but a month EDITED
 *               after its deletion timestamp is resurrected
 *   • overrides — merged, the newer side winning conflicts
 *   • activeId  — from the newer side, if that month still exists
 * Either side may be null/partial. Returns { months, overrides, activeId, trash }.
 */
export function mergeExpenseState(local, cloud, now = Date.now()) {
  const L = local || {}, C = cloud || {};
  const localNewer = (Number(L.updatedAtMs) || 0) >= (Number(C.updatedAtMs) || 0);

  const trashMap = new Map();
  for (const t of [...(C.trash || []), ...(L.trash || [])]) {
    if (!t || !t.id) continue;
    const prev = trashMap.get(t.id);
    if (!prev || (Number(t.deletedAt) || 0) > (Number(prev.deletedAt) || 0)) trashMap.set(t.id, t);
  }

  const monthMap = new Map();
  for (const m of [...(C.months || []), ...(L.months || [])]) {
    if (!m || !m.id) continue;
    const prev = monthMap.get(m.id);
    if (!prev || monthFresh(m) > monthFresh(prev)) monthMap.set(m.id, m);
  }

  const months = [];
  for (const m of monthMap.values()) {
    const t = trashMap.get(m.id);
    if (t && (Number(t.deletedAt) || 0) >= monthFresh(m)) continue; // deleted after last edit → stays deleted
    if (t) trashMap.delete(m.id);                                   // edited after deletion → restored
    months.push(m);
  }
  months.sort((a, b) => (Number(b.savedAt) || 0) - (Number(a.savedAt) || 0));

  const [newer, older] = localNewer ? [L, C] : [C, L];
  const overrides = { ...(older.overrides || {}), ...(newer.overrides || {}) };
  let activeId = newer.activeId || older.activeId || null;
  if (!months.some(m => m.id === activeId)) activeId = months[0]?.id || null;

  return { months, overrides, activeId, trash: purgeTrash([...trashMap.values()], now) };
}
